/**
 * "Sync users now" — the manual trigger for the staff Zoom roster.
 *
 * The roster was ALREADY syncing on its own (fetch_staff_roster_job: daily 02:30 BKK + on every worker
 * boot); what was missing was a button. The principal did not know the cron existed, which is half of
 * why they asked for the button — so the schedule is stated on the panel, unconditionally, whether or
 * not anyone ever clicks.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE: a failed sync must not look like a success.
 *
 * The server hands back a structured outcome and the client does not throw on the non-2xx ones (see
 * api/client.syncRoster), so `mutation.onSuccess` fires for "the Zoom call failed" exactly as it does
 * for "synced 199 staff". Branching on "the request settled" — the natural, wrong thing — would render
 * an outage as a green tick. So EVERY branch here keys off `result.status`, and the one status that
 * gets the ok tone is the literal string "ok". An outcome this build has never heard of is rendered as
 * a FAILURE (`unknown`), not as a blank panel and not as a success: a sync we cannot interpret is a sync
 * we did not verify.
 */

import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { api, ApiRequestError } from "@/api/client";
import { Button, useNotifier } from "@/components/ui";
import { formatInstant, formatInt } from "@/lib/format";
import type { TKey } from "@/i18n/keys";
import { isRosterSyncStatus, type RosterSyncResult, type RosterSyncStatus } from "@/api/types";

/** Every outcome the panel can render — the server's six, plus the one it must invent: `unknown`. */
type Outcome = RosterSyncStatus | "unknown";

/** Only "ok" is a success. Everything else — including a status we don't recognise — is not. */
const TONE: Record<Outcome, "ok" | "warn" | "danger"> = {
  ok: "ok",
  throttled: "warn",
  already_running: "warn",
  zoom_error: "danger",
  empty: "danger",
  // Zoom answered, but with a list so short that trusting it would have evicted a large slice of the
  // staff from the sign-in allow-list. The server refused and rolled the whole rewrite back. `danger`,
  // not `warn`: something is wrong upstream, and the roster is now knowingly not being refreshed.
  suspicious: "danger",
  not_configured: "danger",
  unknown: "danger",
};

// `as const satisfies Record<Outcome, TKey>`, NOT `: Record<Outcome, string>`. The annotation widened
// every value to `string`, which is the one type `t()` refuses — so the keys went unchecked at the map
// (where the typo would live) even though every call site passed through `t()`. `satisfies` checks the
// keys AND keeps their literals, so `t(COPY[outcome])` still knows which interpolation vars it needs.
const COPY = {
  ok: "adminUsers.rosterSync.result.ok",
  throttled: "adminUsers.rosterSync.result.throttled",
  already_running: "adminUsers.rosterSync.result.alreadyRunning",
  zoom_error: "adminUsers.rosterSync.result.zoomError",
  empty: "adminUsers.rosterSync.result.empty",
  suspicious: "adminUsers.rosterSync.result.suspicious",
  not_configured: "adminUsers.rosterSync.result.notConfigured",
  unknown: "adminUsers.rosterSync.result.unknown",
} as const satisfies Record<Outcome, TKey>;

/** The server's raw string → an outcome we can render. Unrecognised ⇒ `unknown` ⇒ a failure. */
function toOutcome(status: string): Outcome {
  return isRosterSyncStatus(status) ? status : "unknown";
}

const PANEL_STYLE: Record<"ok" | "warn" | "danger", string> = {
  ok: "border-(--ok-subtle) bg-(--ok-subtle)",
  warn: "border-(--warn-subtle) bg-(--warn-subtle)",
  danger: "border-(--danger-subtle) bg-(--danger-subtle)",
};

const TITLE_STYLE: Record<"ok" | "warn" | "danger", string> = {
  ok: "text-(--ok)",
  warn: "text-(--warn)",
  danger: "text-(--danger-strong-fg)",
};

export function RosterSyncControl() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotifier();

  const mut = useMutation({
    mutationFn: () => api.syncRoster(),
    onSuccess: (res) => {
      const outcome = toOutcome(res.status);
      if (outcome === "ok") {
        // Only an `ok` rewrote the roster, so only an `ok` can have changed the list under us.
        void qc.invalidateQueries({ queryKey: ["adminUsers"] });
        notify({ tone: "ok", title: t("adminUsers.rosterSync.result.ok", { n: res.synced }) });
        return;
      }
      // Not a success. Loud, and named — never a generic "something went wrong".
      notify({
        tone: TONE[outcome] === "danger" ? "danger" : "warn",
        title: t("adminUsers.rosterSync.failedTitle"),
        body: body(t, outcome, res),
        durationMs: 8000, // auto-dismiss; the inline panel below keeps the durable record
      });
    },
    onError: (e: unknown) => {
      // A THROW here is never a sync outcome — it's a 401/403/proxy/transport failure (the client only
      // throws when the body carries no envelope). Say that, rather than blaming Zoom.
      notify({
        tone: "danger",
        title: t("adminUsers.rosterSync.failedTitle"),
        body:
          e instanceof ApiRequestError && e.error.status !== 0
            ? e.error.message
            : t("state.networkError"),
      });
    },
  });

  const result = mut.data;
  const lastSynced = result?.last_synced_at ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Button variant="secondary" size="sm" loading={mut.isPending} onClick={() => mut.mutate()}>
          {!mut.isPending && <RefreshCw className="size-4" aria-hidden />}
          {t("adminUsers.rosterSync.button")}
        </Button>
        {/* Stated whether or not anyone clicks: the roster is NOT stale between clicks. */}
        <span className="inline-flex items-center gap-1.5 text-(length:--text-xs) text-(--text-muted)">
          <Clock className="size-3.5 shrink-0 text-(--text-subtle)" aria-hidden />
          {t("adminUsers.rosterSync.autoNote")}
        </span>
      </div>

      {lastSynced && (
        <span className="text-(length:--text-2xs) text-(--text-muted)">
          {t("adminUsers.rosterSync.lastSynced", { time: formatInstant(lastSynced) })}
        </span>
      )}

      {result && <ResultPanel result={result} />}

      {mut.isError && !result && (
        <div
          role="alert"
          className="flex flex-col gap-1.5 rounded-(--radius) border border-(--danger-subtle) bg-(--danger-subtle) p-3.5"
        >
          <span className="inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold text-(--danger-strong-fg)">
            <AlertTriangle className="size-4 text-(--danger)" aria-hidden />
            {t("adminUsers.rosterSync.failedTitle")}
          </span>
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {mut.error instanceof ApiRequestError && mut.error.error.status !== 0
              ? mut.error.error.message
              : t("state.networkError")}
          </p>
        </div>
      )}
    </div>
  );
}

// Deliberately NOT `ReturnType<typeof useTranslation>["t"]` — see the identical note in
// SyncNowControl.tsx: that exact overloaded generic type gets expensive enough against a large resource
// tree that adding the Pool module's ~150 keys pushed this file's t() usage over tsc's "type
// instantiation is excessively deep" limit. Every call below passes a real key + a plain interpolation
// object, so this narrower structural alias is sufficient.
type TFn = (key: TKey, options?: Record<string, unknown>) => string;

function body(t: TFn, outcome: Outcome, r: RosterSyncResult): string {
  if (outcome === "ok") return t(COPY.ok, { n: r.synced });
  if (outcome === "throttled") {
    return t(COPY.throttled, { seconds: r.retry_after_seconds ?? 0 });
  }
  // The number IS the message here: "we refused to remove 196 people" is what tells the operator whether
  // this is Zoom glitching or a real mass departure they need to force through.
  if (outcome === "suspicious") return t(COPY.suspicious, { n: formatInt(r.pruned) });
  // The unknown branch carries the server's OWN sentence: we can't localize an outcome we've never
  // seen, and repeating what the server actually said beats inventing a vaguer one.
  if (outcome === "unknown") return `${t(COPY.unknown)} — ${r.message}`;
  return t(COPY[outcome]);
}

/**
 * The durable record of the last attempt. `role="status"` (polite) ONLY on a success; every other
 * outcome is `role="alert"` — an operator who walked away must not come back to a quiet panel that
 * looks like it worked.
 */
function ResultPanel({ result }: { result: RosterSyncResult }) {
  const { t } = useTranslation();
  const outcome = toOutcome(result.status);
  const tone = TONE[outcome];
  const ok = outcome === "ok";

  return (
    <div
      role={ok ? "status" : "alert"}
      className={`flex flex-col gap-1.5 rounded-(--radius) border p-3.5 ${PANEL_STYLE[tone]}`}
    >
      <span
        className={`inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold ${TITLE_STYLE[tone]}`}
      >
        {ok ? (
          <CheckCircle2 className="size-4" aria-hidden />
        ) : (
          <AlertTriangle
            className={`size-4 ${tone === "danger" ? "text-(--danger)" : ""}`}
            aria-hidden
          />
        )}
        {body(t, outcome, result)}
      </span>

      {/* The server's own statement that nobody lost access. Rendered FROM the field, not inferred from
          the status — if the two ever disagree, the server is the one that knows. */}
      {result.roster_intact && (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          {t("adminUsers.rosterSync.rosterIntact", { n: formatInt(result.roster_size) })}
        </p>
      )}
    </div>
  );
}
